"use client";

import { useEffect, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  daysInHebrewYear,
  fixedFromHebrew,
  type HebrewDate,
  type HebrewMonthName,
  hebrewFromFixed,
  hebrewHolidays,
  hebrewMonthName,
  hebrewMonthsOfYear,
  hebrewNewYear,
  hebrewYearType,
  isHebrewLeapYear,
  isValidHebrewDate,
  lastDayOfHebrewMonth,
  lastMonthOfHebrewYear,
} from "@/lib/tool-kit/calendars/hebrew";
import {
  isoFromRd,
  parseGregorianIso,
  RD_LAST_GREGORIAN,
  rdFromGregorian,
  utcDateFromRd,
} from "@/lib/tool-kit/calendars/rd";
import { julianFromDays } from "@/lib/tool-kit/converters/hijri";
import { daysFromCivil } from "@/lib/tool-kit/converters/week-number";

/** The month names in Hebrew script. Data, not prose. */
const HEBREW_MONTHS: Record<HebrewMonthName, string> = {
  nisan: "ניסן",
  iyyar: "אייר",
  sivan: "סיוון",
  tammuz: "תמוז",
  av: "אב",
  elul: "אלול",
  tishri: "תשרי",
  marheshvan: "חשוון",
  kislev: "כסלו",
  tevet: "טבת",
  shevat: "שבט",
  adar: "אדר",
  adarI: "אדר א׳",
  adarII: "אדר ב׳",
};

const FIRST_HEBREW_YEAR = 3761;
const LAST_HEBREW_YEAR = 13_760;
/** 15 October 1582 as an R.D.: the first Gregorian day. */
const GREGORIAN_REFORM_RD = rdFromGregorian(1582, 10, 15);

/** English strings. A translated content file overrides these through `ui`. */
export const HEBREW_CALENDAR_CONVERTER_STRINGS = {
  direction: "Convert",
  toHebrew: "Gregorian → Hebrew",
  toGregorian: "Hebrew → Gregorian",
  gregorianDate: "Gregorian date",
  today: "Today",
  afterSunset: "It happened after sunset (the Hebrew day has already turned)",
  hebrewDay: "Day",
  hebrewMonth: "Month",
  hebrewYear: "Year (AM)",
  monthNames: {
    nisan: "Nisan",
    iyyar: "Iyyar",
    sivan: "Sivan",
    tammuz: "Tammuz",
    av: "Av",
    elul: "Elul",
    tishri: "Tishri",
    marheshvan: "Marheshvan",
    kislev: "Kislev",
    tevet: "Tevet",
    shevat: "Shevat",
    adar: "Adar",
    adarI: "Adar I",
    adarII: "Adar II",
  },
  hebrewDate: "{day} {month} {year}",
  hebrewLabel: "Hebrew date",
  hebrewScriptLabel: "In Hebrew",
  gregorianLabel: "Gregorian date",
  weekdayLabel: "Day of the week (daytime)",
  isoLabel: "ISO 8601",
  julianLabel: "Julian calendar",
  sunsetBefore:
    "Before sunset on {date} it is still {hebrew}; the Hebrew day turns at sunset.",
  sunsetAfter:
    "After sunset on {date} it is already {hebrew}, the next Hebrew day.",
  sunsetBegins:
    "{hebrew} begins at sunset on {evening} and lasts until nightfall on {date}.",
  yearLeap:
    "{year} is a leap year: 13 months and {days} days ({type}). It begins on {start} and ends on {end}.",
  yearCommon:
    "{year} is a common year: 12 months and {days} days ({type}). It begins on {start} and ends on {end}.",
  yearTypes: {
    deficient: "deficient",
    regular: "regular",
    complete: "complete",
  },
  monthSpan: "{month} {year} has {length} days: {start} to {end}.",
  julianNote:
    "Dates before 15 October 1582 are entered and shown in the proleptic Gregorian calendar; historians usually quote the Julian date shown here.",
  early:
    "The calendar's present rules were fixed in the fourth century CE; earlier dates extend them backwards and are conventional.",
  sunsetNote:
    "The Hebrew day begins at sunset, which depends on where you are. This converter gives the date in force during the daytime; tick the box if the moment you care about was after dark.",
  pickDate: "Pick a date to convert.",
  invalidGregorian:
    "Choose a real date between 1 January 1 and 31 December 9999.",
  enterHebrew: "Enter a Hebrew day, month and year.",
  invalidHebrewYear: "Enter a Hebrew year from 3761 to 13760.",
  noSuchMonth: "{year} is not a leap year, so there is no Adar II.",
  noSuchDay:
    "{month} {year} has only {length} days, so day {day} does not exist.",
  outsideRange: "That date falls outside 1 CE to 9999 CE.",
  monthsHeading: "The months of {year}",
  monthColumn: "Month",
  hebrewColumn: "Hebrew",
  startsColumn: "First day",
  daysColumn: "Days",
  holidaysHeading: "Holidays of {year}",
  holidayColumn: "Holiday",
  eveningColumn: "Begins at sunset on",
  dayColumn: "First full day",
  holidays: {
    roshHashanah: "Rosh Hashanah (1 Tishri)",
    yomKippur: "Yom Kippur (10 Tishri)",
    sukkot: "Sukkot (15 Tishri)",
    hanukkah: "Hanukkah (25 Kislev)",
    purim: "Purim (14 Adar)",
    passover: "Passover (15 Nisan)",
    shavuot: "Shavuot (6 Sivan)",
  },
  holidaysNote:
    "Dates are the first day as kept outside Israel; each holiday begins at sunset on the evening before. Observance is calculated, not announced by any authority.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{hebrew} = {gregorian}",
} as const;

type Strings = typeof HEBREW_CALENDAR_CONVERTER_STRINGS;
type Direction = "to-hebrew" | "to-gregorian";
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const LONG_DATE: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
};

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
};

function useFormatters(strings: Strings) {
  const locale = useToolLocale();
  return {
    long: (rd: number) => formatDateTime(locale, utcDateFromRd(rd), LONG_DATE),
    short: (rd: number) =>
      formatDateTime(locale, utcDateFromRd(rd), SHORT_DATE),
    weekday: (rd: number) =>
      formatDateTime(locale, utcDateFromRd(rd), {
        weekday: "long",
        timeZone: "UTC",
      }),
    julian: (rd: number) => {
      const j = julianFromDays(rd - 719_163);
      return formatDateTime(
        locale,
        new Date(daysFromCivil(j.year, j.month, j.day) * 86_400_000),
        { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }
      );
    },
    hebrew: (date: HebrewDate) =>
      t(strings.hebrewDate, {
        day: String(date.day),
        month: strings.monthNames[hebrewMonthName(date.year, date.month)],
        year: String(date.year),
      }),
  };
}

type Formatters = ReturnType<typeof useFormatters>;

function hebrewScript(date: HebrewDate): string {
  return `${date.day} ב${HEBREW_MONTHS[hebrewMonthName(date.year, date.month)]} ${date.year}`;
}

interface Reading {
  /** The civil day whose daytime the result describes. */
  rd: number;
  hebrew: HebrewDate;
  afterSunset: boolean;
}

function ContextLines({
  reading,
  direction,
  strings,
  format,
}: {
  reading: Reading;
  direction: Direction;
  strings: Strings;
  format: Formatters;
}) {
  const { rd, hebrew, afterSunset } = reading;
  const lines: string[] = [];
  if (direction === "to-gregorian") {
    lines.push(
      t(strings.sunsetBegins, {
        hebrew: format.hebrew(hebrew),
        evening: format.short(rd - 1),
        date: format.short(rd),
      })
    );
  } else {
    const dayHebrew = hebrewFromFixed(rd);
    const nightHebrew = hebrewFromFixed(rd + 1);
    lines.push(
      afterSunset
        ? t(strings.sunsetBefore, {
            date: format.short(rd),
            hebrew: format.hebrew(dayHebrew),
          })
        : t(strings.sunsetAfter, {
            date: format.short(rd),
            hebrew: format.hebrew(nightHebrew),
          })
    );
  }
  if (rd < GREGORIAN_REFORM_RD) {
    lines.push(strings.julianNote);
  }
  if (hebrew.year < 4120) {
    lines.push(strings.early);
  }
  return (
    <ul className="space-y-1 text-foreground text-sm leading-relaxed">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

function YearPanel({
  year,
  month,
  strings,
  format,
  fmt,
}: {
  year: number;
  month: number;
  strings: Strings;
  format: Formatters;
  fmt: Fmt;
}) {
  const months = hebrewMonthsOfYear(year);
  const info = months.find((entry) => entry.month === month);
  const length = daysInHebrewYear(year);
  const start = hebrewNewYear(year);
  const yearLine = t(
    isHebrewLeapYear(year) ? strings.yearLeap : strings.yearCommon,
    {
      year: String(year),
      days: fmt(length),
      type: strings.yearTypes[hebrewYearType(year)],
      start: format.short(start),
      end: format.short(start + length - 1),
    }
  );
  const monthLine = info
    ? t(strings.monthSpan, {
        month: strings.monthNames[info.name],
        year: String(year),
        length: fmt(info.length),
        start: format.short(info.start),
        end: format.short(info.start + info.length - 1),
      })
    : "";
  return (
    <>
      <ul className="space-y-1 text-foreground text-sm leading-relaxed">
        <li>{monthLine}</li>
        <li>{yearLine}</li>
      </ul>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>
          {t(strings.monthsHeading, { year: String(year) })}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={TH} scope="col">
                  {strings.monthColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.hebrewColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.startsColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.daysColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {months.map((entry) => (
                <tr
                  className={
                    entry.month === month ? "bg-primary/10 font-medium" : ""
                  }
                  key={entry.month}
                >
                  <td className={TD}>{strings.monthNames[entry.name]}</td>
                  <td className={TD} dir="rtl" lang="he">
                    {HEBREW_MONTHS[entry.name]}
                  </td>
                  <td className={TD}>{format.short(entry.start)}</td>
                  <td className={TD}>{fmt(entry.length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <HolidayTable format={format} strings={strings} year={year} />
    </>
  );
}

function HolidayTable({
  year,
  strings,
  format,
}: {
  year: number;
  strings: Strings;
  format: Formatters;
}) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>
        {t(strings.holidaysHeading, { year: String(year) })}
      </h2>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.holidayColumn}
              </th>
              <th className={TH} scope="col">
                {strings.eveningColumn}
              </th>
              <th className={TH} scope="col">
                {strings.dayColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {hebrewHolidays(year).map((entry) => (
              <tr key={entry.holiday}>
                <td className={TD}>{strings.holidays[entry.holiday]}</td>
                <td className={TD}>{format.short(entry.day - 1)}</td>
                <td className={TD}>{format.long(entry.day)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-sm">{strings.holidaysNote}</p>
    </section>
  );
}

function ResultPanel({
  reading,
  direction,
  strings,
}: {
  reading: Reading;
  direction: Direction;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const format = useFormatters(strings);
  const { rd, hebrew } = reading;
  const hebrewText = format.hebrew(hebrew);
  const gregorianText = format.long(rd);
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {direction === "to-hebrew" ? hebrewText : gregorianText}
        </h2>
        <StatGrid>
          <StatCard
            emphasis={direction === "to-hebrew"}
            label={strings.hebrewLabel}
            value={hebrewText}
          />
          <StatCard
            label={strings.hebrewScriptLabel}
            value={
              <span dir="rtl" lang="he">
                {hebrewScript(hebrew)}
              </span>
            }
          />
          <StatCard
            emphasis={direction === "to-gregorian"}
            label={strings.gregorianLabel}
            value={format.short(rd)}
          />
          <StatCard label={strings.weekdayLabel} value={format.weekday(rd)} />
          <StatCard label={strings.isoLabel} value={isoFromRd(rd)} />
          {rd < GREGORIAN_REFORM_RD ? (
            <StatCard label={strings.julianLabel} value={format.julian(rd)} />
          ) : null}
        </StatGrid>
        <ContextLines
          direction={direction}
          format={format}
          reading={reading}
          strings={strings}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            hebrew: hebrewText,
            gregorian: gregorianText,
          })}
        />
      </section>
      <Notice>{strings.sunsetNote}</Notice>
      <YearPanel
        fmt={fmt}
        format={format}
        month={hebrew.month}
        strings={strings}
        year={hebrew.year}
      />
    </>
  );
}

interface HebrewFields {
  day: string;
  month: string;
  year: string;
}

type Outcome = { reading: Reading } | { error: string } | null;

function readGregorian(
  text: string,
  afterSunset: boolean,
  strings: Strings
): Outcome {
  if (text.trim() === "") {
    return null;
  }
  const rd = parseGregorianIso(text);
  if (rd === null) {
    return { error: strings.invalidGregorian };
  }
  const hebrew = hebrewFromFixed(afterSunset ? rd + 1 : rd);
  return { reading: { rd, hebrew, afterSunset } };
}

function readHebrew(fields: HebrewFields, strings: Strings, fmt: Fmt): Outcome {
  if (fields.day.trim() === "" || fields.year.trim() === "") {
    return null;
  }
  const day = parseIntegerInput(fields.day);
  const month = Number(fields.month);
  const year = parseIntegerInput(fields.year);
  if (year === null || year < FIRST_HEBREW_YEAR || year > LAST_HEBREW_YEAR) {
    return { error: strings.invalidHebrewYear };
  }
  if (day === null || day < 1 || day > 30) {
    return { error: strings.enterHebrew };
  }
  if (month > lastMonthOfHebrewYear(year)) {
    return { error: t(strings.noSuchMonth, { year: String(year) }) };
  }
  const date = { year, month, day };
  if (!isValidHebrewDate(date)) {
    return {
      error: t(strings.noSuchDay, {
        month: strings.monthNames[hebrewMonthName(year, month)],
        year: String(year),
        length: fmt(lastDayOfHebrewMonth(year, month)),
        day: fmt(day),
      }),
    };
  }
  const rd = fixedFromHebrew(date);
  if (rd < 1 || rd > RD_LAST_GREGORIAN) {
    return { error: strings.outsideRange };
  }
  return { reading: { rd, hebrew: date, afterSunset: false } };
}

/** Today on the reader's own clock — only ever called after hydration. */
function todayRd(): number {
  const now = new Date();
  return rdFromGregorian(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Month choices: Tishri first; 12 reads Adar I in a leap year and Adar in any other. */
function monthOptions(year: number | null, strings: Strings) {
  const reference = year !== null && isHebrewLeapYear(year) ? 5784 : 5785;
  return [7, 8, 9, 10, 11, 12, 13, 1, 2, 3, 4, 5, 6].map((month) => ({
    value: String(month),
    label: strings.monthNames[hebrewMonthName(reference, month)],
  }));
}

export default function HebrewCalendarConverterWidget() {
  const strings = useToolUi(HEBREW_CALENDAR_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [direction, setDirection] = useState<Direction>("to-hebrew");
  const [gregorian, setGregorian] = useState("");
  const [afterSunset, setAfterSunset] = useState(false);
  const [fields, setFields] = useState<HebrewFields>({
    day: "",
    month: "7",
    year: "",
  });

  useEffect(() => {
    const today = todayRd();
    const hebrew = hebrewFromFixed(today);
    setGregorian((current) => current || isoFromRd(today));
    setFields((current) =>
      current.year === ""
        ? {
            day: String(hebrew.day),
            month: String(hebrew.month),
            year: String(hebrew.year),
          }
        : current
    );
  }, []);

  const outcome =
    direction === "to-hebrew"
      ? readGregorian(gregorian, afterSunset, strings)
      : readHebrew(fields, strings, fmt);
  const setField = (key: keyof HebrewFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));
  const typedYear = parseIntegerInput(fields.year);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="hc-direction"
        onChange={setDirection}
        options={[
          { value: "to-hebrew", label: strings.toHebrew },
          { value: "to-gregorian", label: strings.toGregorian },
        ]}
        value={direction}
      />
      {direction === "to-hebrew" ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <TextField
                id="hc-gregorian"
                label={strings.gregorianDate}
                onChange={setGregorian}
                type="date"
                value={gregorian}
              />
            </div>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setGregorian(isoFromRd(todayRd()))}
              type="button"
            >
              {strings.today}
            </button>
          </div>
          <Checkbox
            checked={afterSunset}
            label={strings.afterSunset}
            onChange={setAfterSunset}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr]">
          <TextField
            id="hc-day"
            label={strings.hebrewDay}
            numeric
            onChange={setField("day")}
            value={fields.day}
          />
          <SelectField
            id="hc-month"
            label={strings.hebrewMonth}
            onChange={setField("month")}
            options={monthOptions(typedYear, strings)}
            value={fields.month}
          />
          <TextField
            id="hc-year"
            label={strings.hebrewYear}
            numeric
            onChange={setField("year")}
            value={fields.year}
          />
        </div>
      )}
      {outcome === null ? (
        <Notice>
          {direction === "to-hebrew" ? strings.pickDate : strings.enterHebrew}
        </Notice>
      ) : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "reading" in outcome ? (
        <ResultPanel
          direction={direction}
          reading={outcome.reading}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
